/**
 * =====================================================================================
 * SERVICIO DE EXPORTACIÓN - export.service.js
 * =====================================================================================
 *
 * Servicio para exportar datos a Excel (.xlsx).
 * Conecta con el ExportController del backend.
 *
 * ENDPOINTS DISPONIBLES:
 * ----------------------
 * GET /api/export/facturas/excel    - Exporta facturas (fechaInicio?, fechaFin?)
 * GET /api/export/inventario/excel  - Exporta inventario completo
 * GET /api/export/ventas/excel      - Exporta reporte de ventas (fechaInicio, fechaFin requeridos)
 *
 * NOTAS TÉCNICAS:
 * ---------------
 * - Los endpoints devuelven archivos .xlsx (blob)
 * - MIME type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
 * - Se requiere responseType: 'blob' en las peticiones
 * - El backend genera los archivos con ClosedXML
 *
 * =====================================================================================
 */

import api from './api'

/**
 * Descarga un archivo blob como archivo del sistema
 * @param {Blob} blob - El blob del archivo
 * @param {string} filename - Nombre del archivo a descargar
 */
function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

/**
 * Extrae el nombre del archivo del header Content-Disposition
 * @param {Object} headers - Headers de la respuesta
 * @param {string} defaultName - Nombre por defecto si no se encuentra
 * @returns {string} Nombre del archivo
 */
function getFilenameFromHeaders(headers, defaultName) {
  const contentDisposition = headers['content-disposition']
  if (contentDisposition) {
    // Buscar filename en el header: attachment; filename=Facturas_20251223.xlsx
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    if (match && match[1]) {
      return match[1].replace(/['"]/g, '')
    }
  }
  return defaultName
}

const exportService = {
  /**
   * Exporta facturas a Excel
   *
   * @param {string|null} fechaInicio - Fecha inicio (formato: YYYY-MM-DD), opcional
   * @param {string|null} fechaFin - Fecha fin (formato: YYYY-MM-DD), opcional
   * @returns {Promise<void>} Descarga el archivo automáticamente
   *
   * Si no se especifican fechas, el backend usa el último mes por defecto.
   * Archivo generado: Facturas_yyyyMMdd.xlsx
   */
  async exportarFacturas(fechaInicio = null, fechaFin = null) {
    const params = {}
    if (fechaInicio) params.fechaInicio = fechaInicio
    if (fechaFin) params.fechaFin = fechaFin

    const response = await api.get('/export/facturas/excel', {
      params,
      responseType: 'blob'
    })

    const filename = getFilenameFromHeaders(
      response.headers,
      (() => { const d = new Date(), p = (n) => String(n).padStart(2, '0'); return `Facturas_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}.xlsx` })()
    )
    downloadBlob(response.data, filename)
  },

  /**
   * Exporta inventario completo a Excel
   *
   * @returns {Promise<void>} Descarga el archivo automáticamente
   *
   * No requiere parámetros, exporta todo el inventario actual.
   * Archivo generado: Inventario_yyyyMMdd.xlsx
   */
  async exportarInventario() {
    const response = await api.get('/export/inventario/excel', {
      responseType: 'blob'
    })

    const filename = getFilenameFromHeaders(
      response.headers,
      (() => { const d = new Date(), p = (n) => String(n).padStart(2, '0'); return `Inventario_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}.xlsx` })()
    )
    downloadBlob(response.data, filename)
  },

  /**
   * Exporta reporte de ventas a Excel
   *
   * @param {string} fechaInicio - Fecha inicio (formato: YYYY-MM-DD) - REQUERIDO
   * @param {string} fechaFin - Fecha fin (formato: YYYY-MM-DD) - REQUERIDO
   * @returns {Promise<void>} Descarga el archivo automáticamente
   * @throws {Error} Si no se proporcionan las fechas
   *
   * Archivo generado: Ventas_yyyyMMdd_yyyyMMdd.xlsx
   */
  async exportarVentas(fechaInicio, fechaFin) {
    if (!fechaInicio || !fechaFin) {
      throw new Error('Se requieren fechaInicio y fechaFin para exportar ventas')
    }

    const response = await api.get('/export/ventas/excel', {
      params: { fechaInicio, fechaFin },
      responseType: 'blob'
    })

    const filename = getFilenameFromHeaders(
      response.headers,
      `Ventas_${fechaInicio.replace(/-/g, '')}_${fechaFin.replace(/-/g, '')}.xlsx`
    )
    downloadBlob(response.data, filename)
  },

  /**
   * Descarga plantilla Excel para carga masiva de productos
   * @returns {Promise<void>} Descarga el archivo automáticamente
   */
  async descargarPlantillaProductos() {
    const response = await api.get('/export/plantilla/productos', {
      responseType: 'blob'
    })

    const filename = getFilenameFromHeaders(
      response.headers,
      (() => { const d = new Date(), p = (n) => String(n).padStart(2, '0'); return `Plantilla_Productos_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}.xlsx` })()
    )
    downloadBlob(response.data, filename)
  },

  /**
   * Descarga plantilla Excel para carga masiva de servicios
   * @returns {Promise<void>} Descarga el archivo automáticamente
   */
  async descargarPlantillaServicios() {
    const response = await api.get('/export/plantilla/servicios', {
      responseType: 'blob'
    })

    const filename = getFilenameFromHeaders(
      response.headers,
      (() => { const d = new Date(), p = (n) => String(n).padStart(2, '0'); return `Plantilla_Servicios_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}.xlsx` })()
    )
    downloadBlob(response.data, filename)
  },

  /**
   * Importa productos desde un archivo Excel
   * @param {File} archivo - Archivo Excel (.xlsx)
   * @returns {Promise<Object>} Resultado de la importación { totalFilas, exitosos, fallidos, categoriasCreadas, marcasCreadas, errores }
   */
  async importarProductos(archivo) {
    const formData = new FormData()
    formData.append('archivo', archivo)

    const response = await api.post('/import/productos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  /**
   * Importa servicios desde un archivo Excel
   * @param {File} archivo - Archivo Excel (.xlsx)
   * @returns {Promise<Object>} Resultado de la importación { totalFilas, exitosos, fallidos, categoriasCreadas, errores }
   */
  async importarServicios(archivo) {
    const formData = new FormData()
    formData.append('archivo', archivo)

    const response = await api.post('/import/servicios', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  }
}

export default exportService
